<template>
  <v-container>
    <v-row
      class="mb-2"
      align="center"
    >
      <v-col>
        <h2>ຄົ້ນຫາ QR ດ້ວຍ Bill Number</h2>
        <div class="grey--text text--darken-1 text-body-2">
          ຄົ້ນຫາລາຍການ LaoQR ໃນ qr_generator (ພິມບາງສ່ວນຂອງເລກກໍໄດ້)
        </div>
      </v-col>
    </v-row>

    <v-card
      class="mb-4"
      elevation="2"
    >
      <v-card-text>
        <v-form @submit.prevent="search">
          <v-row align="center">
            <v-col
              cols="12"
              md="8"
            >
              <v-text-field
                v-model.trim="billNumber"
                label="Bill Number"
                placeholder="ເຊັ່ນ CC60C2D291D9"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details="auto"
                :rules="[(v) => !v || v.length >= 3 || 'ພິມຢ່າງໜ້ອຍ 3 ຕົວອັກສອນ']"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-btn
                color="primary"
                type="submit"
                block
                :loading="loading"
                :disabled="!billNumber || billNumber.length < 3"
              >
                <v-icon left>
                  mdi-magnify
                </v-icon>
                ຄົ້ນຫາ
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card
      v-if="searched"
      elevation="2"
    >
      <v-card-title class="text-subtitle-1">
        ພົບ {{ results.length }} ລາຍການ
        <span
          v-if="results.length >= limit"
          class="ml-2 orange--text text-body-2"
        >
          (ສະແດງສູງສຸດ {{ limit }} ລາຍການ, ພິມໃຫ້ລະອຽດຂຶ້ນເພື່ອຈຳກັດຜົນ)
        </span>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="results"
        :loading="loading"
        item-key="id"
        hide-default-footer
        disable-pagination
        no-data-text="ບໍ່ພົບຂໍ້ມູນ"
        class="qr-table"
      >
        <template #[`item.bill_number`]="{ item }">
          <span class="font-weight-bold">{{ item.bill_number }}</span>
          <v-chip
            v-if="item.deleted_at"
            x-small
            color="grey"
            dark
            class="ml-1"
          >
            ຖືກລຶບ
          </v-chip>
        </template>
        <template #[`item.owner`]="{ item }">
          <div v-if="item.owner">
            <div>{{ item.owner.name }}</div>
            <div class="grey--text text-caption">
              {{ item.owner.phone }}
              <span v-if="item.owner.customer && item.owner.customer.display_id"> · {{ item.owner.customer.display_id }}</span>
            </div>
          </div>
          <span v-else>-</span>
        </template>
        <template #[`item.billing_ids`]="{ item }">
          <span class="text-caption">{{ item.billing_ids.join(', ') || '-' }}</span>
        </template>
        <template #[`item.amount`]="{ item }">
          {{ fmtNumber(item.amount) }} {{ item.currency }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            small
            label
            dark
            :color="statusColor(item.status)"
          >
            {{ item.status }}
          </v-chip>
        </template>
        <template #[`item.paid_at`]="{ item }">
          {{ fmtDate(item.paid_at) }}
        </template>
        <template #[`item.expired_at`]="{ item }">
          {{ fmtDate(item.expired_at) }}
        </template>
        <template #[`item.created_at`]="{ item }">
          {{ fmtDate(item.created_at) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import moment from 'moment';

export default {
  name: 'QrBillNumberSearch',
  data() {
    return {
      billNumber: '',
      results: [],
      loading: false,
      searched: false,
      limit: 50,
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Bill Number', value: 'bill_number' },
        { text: 'ລູກຄ້າ', value: 'owner', sortable: false },
        { text: 'Billing IDs', value: 'billing_ids', sortable: false },
        { text: 'ຈຳນວນເງິນ', value: 'amount', align: 'end' },
        { text: 'ທະນາຄານ', value: 'acquirer' },
        { text: 'ສະຖານະ', value: 'status' },
        { text: 'ຊຳລະເມື່ອ', value: 'paid_at' },
        { text: 'ໝົດອາຍຸ', value: 'expired_at' },
        { text: 'ສ້າງເມື່ອ', value: 'created_at' },
        { text: 'Request ID', value: 'request_id', sortable: false },
      ],
    };
  },
  created() {
    if (this.$route.query.bill_number) {
      this.billNumber = String(this.$route.query.bill_number);
      this.search();
    }
  },
  methods: {
    search() {
      if (!this.billNumber || this.billNumber.length < 3) return;

      if (this.$route.query.bill_number !== this.billNumber) {
        this.$router.replace({ query: { bill_number: this.billNumber } }).catch(() => {});
      }

      this.loading = true;
      this.$axios
        .get('qr-generator/search', { params: { bill_number: this.billNumber, limit: this.limit } })
        .then((res) => {
          this.results = res.data.data || [];
          this.searched = true;
        })
        .catch(() => {
          this.results = [];
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'ຄົ້ນຫາບໍ່ສຳເລັດ',
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    statusColor(status) {
      return {
        COMPLETED: 'green',
        PENDING: 'orange',
        CANCELED: 'grey',
        FAILED: 'red',
      }[status] || 'blue-grey';
    },
    fmtNumber(val) {
      if (val === null || val === undefined || val === '') return '-';
      return Intl.NumberFormat().format(Number(val));
    },
    fmtDate(val) {
      return val ? moment(val).format('DD-MM-YYYY HH:mm') : '-';
    },
  },
};
</script>

<style scoped>
.qr-table >>> td {
  white-space: nowrap;
}
</style>
